import useAxios from "axios-hooks";
import React, { useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import {
  Button,
  Flex,
  Box,
  Heading,
  Icon,
  Text,
  CircularProgress,
  CircularProgressLabel,
  HStack,
  VStack,
  Tooltip,
  useColorModeValue,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import {
  MdStop,
  MdElectricBolt,
  MdBolt,
  MdCable,
  MdUpdate,
  MdOutlineElectricCar,
} from "react-icons/md";
import { motion } from "framer-motion";

import { toastState } from "atoms/toastState";
import { activeConnectorState } from "atoms/activeConnectorState";
import logger from "infra/logger";

const GlassMorphismCard = styled(Box)`
  backdrop-filter: blur(16px);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  padding: 0.75rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(245, 125, 54, 0.25);

  @media (min-width: 768px) {
    padding: 1rem;
  }

  &:hover {
    border-color: rgba(245, 125, 54, 0.4);
  }
`;

const StatCard = styled(Box)`
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(8px);
  border-radius: 12px;
  padding: 0.625rem;
  border: 1px solid rgba(245, 125, 54, 0.12);
  transition: all 0.3s ease;
  flex: 1;
  min-width: 110px;

  @media (min-width: 768px) {
    padding: 0.75rem;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.7);
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(245, 125, 54, 0.1);
  }
`;

const PulseButton = styled(Button)`
  position: relative;
  overflow: hidden;
  transform: translateZ(0);

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.2);
    transform: scale(0);
    opacity: 0;
    border-radius: inherit;
  }

  &:hover:before {
    animation: pulse 0.8s ease-out;
  }

  @keyframes pulse {
    0% {
      transform: scale(0);
      opacity: 0.5;
    }
    100% {
      transform: scale(2);
      opacity: 0;
    }
  }
`;

const OngoingSession = ({ refresh, connector: connectorProp }) => {
  const activeConnectorFromState = useRecoilValue(activeConnectorState);
  const activeConnector = connectorProp ?? activeConnectorFromState;

  const setToast = useSetRecoilState(toastState);
  const [session, setSession] = useState(null);
  const [{ loading }, execute] = useAxios(
    {
      method: "GET",
      url: `/api/chargingSession/ongoing?evseUid=${activeConnector?.EvseUid}`,
    },
    { manual: true }
  );
  const [{ loading: stopLaoding }, executeStop] = useAxios(
    {
      method: "POST",
      url: `/api/ocpp16/charging/stop`,
    },
    { manual: true }
  );

  const getOngoingSesson = async () => {
    try {
      const response = await execute();
      setSession(response.data.data);
    } catch (error) {
      logger.log(error);
    }
  };

  const stopSession = async () => {
    const payload = {
      StationId: activeConnector.EvseId,
      TransactionId: session.Id,
    };

    try {
      const response = await executeStop({ data: payload });
      setToast({
        isOpen: true,
        title: "Session Stopped",
        status: "success",
      });
    } catch (error) {
      const err = error?.response?.data?.error;
      logger.log(error);
      setToast({
        isOpen: true,
        title: "Error occured when stopping Session",
        description: err ? err : null,
        status: "error",
      });
    }
    refresh();
  };

  useEffect(() => {
    if (activeConnector?.Status === "Charging") {
      getOngoingSesson();
      const intervalId = setInterval(() => {
        getOngoingSesson();
      }, 10000);
      return () => clearInterval(intervalId);
    }
  }, []);

  // Theme colors
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const iconColor = useColorModeValue("#f57d36", "#ff9966");

  const batteryPercentage = session ? session?.ConsumerSoc : 0;
  const energyConsumed = session ? session?.UnitsConsumed : 0;
  const socColor =
    batteryPercentage >= 70
      ? "green.400"
      : batteryPercentage >= 30
      ? "#f57d36"
      : "red.400";

  return (
    <GlassMorphismCard
      width="100%"
      height="100%"
      position="relative"
      overflow="hidden"
      as={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Flex justify="space-between" align="center" mb={{ base: 2.5, md: 3 }}>
        <HStack spacing={2}>
          <Icon
            as={MdElectricBolt}
            color="#f57d36"
            boxSize={4}
          />
          <Heading
            size="sm"
            fontWeight={600}
            color={labelColor}
            fontSize={{ base: "sm", md: "sm" }}
          >
            Ongoing Charging Session
          </Heading>
        </HStack>

        {session && (
          <Tooltip label="Session active" placement="top">
            <HStack spacing={1.5} align="center">
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg="#f57d36"
                as={motion.div}
                animate={{ scale: [1, 1.4, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ repeat: Infinity, duration: 2 }}
              />
              <Text
                fontSize="xs"
                fontWeight={600}
                color={iconColor}
              >
                Live
              </Text>
            </HStack>
          </Tooltip>
        )}
      </Flex>

      {session ? (
        <VStack spacing={{ base: 2.5, md: 3 }} align="stretch">
          <Flex
            direction={{ base: "column", sm: "row" }}
            gap={{ base: 2, md: 2.5 }}
            justify="center"
            align="stretch"
          >
            {/* Consumption Stat */}
            <StatCard>
              <VStack spacing={2} align="center">
                <HStack spacing={2} align="center">
                  <Icon as={MdBolt} color={iconColor} boxSize={4} />
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="600"
                    letterSpacing="0.5px"
                    textTransform="uppercase"
                  >
                    Consumption
                  </Text>
                </HStack>
                <HStack align="baseline" spacing={1}>
                  <Heading
                    size="md"
                    color="#f57d36"
                    fontWeight="bold"
                  >
                    {energyConsumed?.toFixed(2) ?? "0.00"}
                  </Heading>
                  <Text
                    fontSize="sm"
                    color="gray.600"
                    fontWeight="medium"
                  >
                    kWh
                  </Text>
                </HStack>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  Energy consumed
                </Text>
              </VStack>
            </StatCard>

            {/* Battery SOC Stat */}
            <StatCard>
              <VStack spacing={2} align="center">
                <HStack spacing={2} align="center">
                  <Icon as={MdElectricBolt} color={iconColor} boxSize={4} />
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    fontWeight="600"
                    letterSpacing="0.5px"
                    textTransform="uppercase"
                  >
                    Battery SOC
                  </Text>
                </HStack>
                <Box mt={1}>
                  <CircularProgress
                    value={batteryPercentage ?? 0}
                    size="72px"
                    thickness="8px"
                    color={socColor}
                    trackColor="rgba(245, 125, 54, 0.1)"
                    capIsRound
                  >
                    <CircularProgressLabel
                      fontSize="sm"
                      fontWeight="bold"
                      color={labelColor}
                    >
                      {batteryPercentage ?? 0}%
                    </CircularProgressLabel>
                  </CircularProgress>
                </Box>
                <Text fontSize="xs" color="gray.500" textAlign="center">
                  Battery level
                </Text>
              </VStack>
            </StatCard>
          </Flex>

          {session?.Status === "ONGOING" && (
            <Flex justify="center" mt={1}>
              <PulseButton
                bg="linear-gradient(90deg, #e53e3e 0%, #f56565 100%)"
                _hover={{
                  boxShadow: "0 6px 14px rgba(229, 62, 62, 0.3)",
                }}
                _active={{
                  transform: "translateY(0)",
                  boxShadow: "0 3px 6px rgba(229, 62, 62, 0.3)",
                }}
                color="white"
                borderRadius="md"
                px={4}
                onClick={stopSession}
                isLoading={stopLaoding}
                leftIcon={<Icon as={MdStop} boxSize={4} />}
                fontWeight={600}
                fontSize="sm"
                h="36px"
                transition="all 0.3s ease"
                as={motion.button}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                Stop Charging
              </PulseButton>
            </Flex>
          )}
        </VStack>
      ) : (
        <Box py={{ base: 4, md: 5 }}>
          <Flex direction="column" align="center" justify="center" gap={3}>
            <Box position="relative" height="76px" width="76px">
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                height="100%"
                width="100%"
                borderRadius="full"
                bg="rgba(245, 125, 54, 0.1)"
                as={motion.div}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 2.5,
                  ease: "easeInOut",
                }}
              />

              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                as={motion.div}
                animate={{ rotate: 360 }}
                transition={{
                  repeat: Infinity,
                  duration: loading ? 4 : 8,
                  ease: "linear",
                }}
              >
                <Box
                  position="absolute"
                  top="-4px"
                  left="50%"
                  transform="translateX(-50%)"
                  bg="white"
                  p={1}
                  borderRadius="full"
                  boxShadow="0 0 10px rgba(245, 125, 54, 0.5)"
                >
                  <Icon as={MdCable} color="#f57d36" boxSize={3} />
                </Box>
              </Box>

              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                as={motion.div}
                animate={{ rotate: -360 }}
                transition={{
                  repeat: Infinity,
                  duration: loading ? 6 : 12,
                  ease: "linear",
                }}
              >
                <Box
                  position="absolute"
                  bottom="-4px"
                  left="50%"
                  transform="translateX(-50%)"
                  bg="white"
                  p={1}
                  borderRadius="full"
                  boxShadow="0 0 10px rgba(245, 125, 54, 0.5)"
                >
                  <Icon as={MdUpdate} color="#f57d36" boxSize={3} />
                </Box>
              </Box>

              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                height="65%"
                width="65%"
                borderRadius="full"
                bg="rgba(255, 255, 255, 0.9)"
                boxShadow="0 0 15px rgba(245, 125, 54, 0.3)"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={1}
              >
                <Icon as={MdOutlineElectricCar} color="#f57d36" boxSize={5} />
              </Box>
            </Box>

            <Box
              bg="rgba(255, 255, 255, 0.7)"
              backdropFilter="blur(8px)"
              borderRadius="lg"
              px={4}
              py={2.5}
              border="1px solid rgba(245, 125, 54, 0.15)"
              boxShadow="0 4px 12px rgba(0, 0, 0, 0.05)"
              maxW="240px"
              w="100%"
              textAlign="center"
            >
              <Heading
                size="xs"
                color="#f57d36"
                fontWeight="600"
                mb={1}
                textAlign="center"
              >
                Waiting for data
              </Heading>
              <Text
                color="gray.600"
                fontWeight="medium"
                textAlign="center"
                fontSize="xs"
              >
                Waiting for the charger to update the session or gun status
              </Text>
            </Box>

            <HStack spacing={1.5} justify="center">
              {[0, 1, 2].map((i) => (
                <Box
                  key={i}
                  h="6px"
                  w="6px"
                  borderRadius="full"
                  bg="#f57d36"
                  boxShadow="0 0 6px rgba(245, 125, 54, 0.5)"
                  as={motion.div}
                  animate={{
                    opacity: [0.3, 1, 0.3],
                    scale: [0.8, 1.2, 0.8],
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    delay: i * 0.2,
                    ease: "easeInOut",
                  }}
                />
              ))}
            </HStack>
          </Flex>
        </Box>
      )}
    </GlassMorphismCard>
  );
};

export default OngoingSession;
